import _ from 'lodash-es'
import { DRAW_CONSTS } from './constants-canvas'
import { mod, isBetween, easeBell } from '../../lib/math-utils'
import { countriesData } from './data'
import { useRef, useEffect } from 'react'
import { MOBILE_BREAKPOINT } from './draw/draw-commons'
import { INERTIA } from './constants'

export const usePositions = (parentWidth: number) => {
  const positions = useRef({
    canvasWidth: parentWidth,

    get globalStartingXPos() {
      return this.canvasWidth / 2 - DRAW_CONSTS.GLOBAL_WIDTH / 2
    },

    get countriesStartingXPos() {
      return this.globalStartingXPos + DRAW_CONSTS.GLOBAL_WIDTH + DRAW_CONSTS.GAP
    },

    get totalLength() {
      return (
        DRAW_CONSTS.GLOBAL_WIDTH +
        DRAW_CONSTS.GAP +
        countriesData.length * (DRAW_CONSTS.GAP + DRAW_CONSTS.COUNTRY_WIDTH)
      )
    },

    translate(pos: number, elWidth: number = 0) {
      return mod(pos + elWidth, this.totalLength) - elWidth
    },

    getGlobalXPos(scroll: number) {
      return this.translate(this.globalStartingXPos - scroll, DRAW_CONSTS.GLOBAL_WIDTH)
    },

    getCountryXPos(scroll: number, index: number) {
      const pos = this.countriesStartingXPos + index * (DRAW_CONSTS.GAP + DRAW_CONSTS.COUNTRY_WIDTH)
      return this.translate(pos - scroll, DRAW_CONSTS.COUNTRY_WIDTH)
    },

    getElementXPos(scroll: number, element: number | 'global') {
      return element === 'global'
        ? this.getGlobalXPos(scroll)
        : this.getCountryXPos(scroll, element)
    },

    getElementCenterPos(target: number | 'global', scroll: number) {
      const xPos = this.getElementXPos(scroll, target)
      const elWidth = target === 'global' ? DRAW_CONSTS.GLOBAL_WIDTH : DRAW_CONSTS.COUNTRY_WIDTH

      const centerPos = xPos + elWidth / 2

      return centerPos
    },

    getElementFromPos(pos: number, scroll: number) {
      return this.getFocusedElement(scroll + pos - positions.canvasWidth / 2)
    },

    getFocusedElement(scroll: number, speed: number = 0): number | 'global' {
      const inertia = this.translate(scroll) + speed * INERTIA

      const centerOffset = this.countriesStartingXPos - this.canvasWidth / 2
      const countryOffset = DRAW_CONSTS.COUNTRY_WIDTH / 2
      const offset = centerOffset + countryOffset

      const actualScroll = inertia - offset
      const perc = actualScroll / (DRAW_CONSTS.COUNTRY_WIDTH + DRAW_CONSTS.GAP)
      const index = _.round(perc)

      return isBetween([0, countriesData.length - 1], index) ? index : 'global'
    },

    getScrollToFocusTarget(
      target: number | 'global',
      currentScroll: number,
      direction: -1 | 0 | 1 = 0
    ) {
      const focusedElement = this.getFocusedElement(currentScroll)

      const elCenterPos = this.getElementCenterPos(target, currentScroll)

      const [left, right] =
        elCenterPos >= this.canvasWidth / 2
          ? [elCenterPos - this.totalLength, elCenterPos]
          : [elCenterPos, elCenterPos + this.totalLength]

      const x =
        focusedElement === target
          ? elCenterPos
          : {
              [-1]: left,
              1: right,
              0: _.minBy([left, right], (n) => Math.abs(n - this.canvasWidth / 2)),
            }[direction]

      return currentScroll + x - this.canvasWidth / 2
    },

    getOpacityFromPos(x: number) {
      if (positions.canvasWidth < MOBILE_BREAKPOINT) return DRAW_CONSTS.MAX_OPACITY
      const step = DRAW_CONSTS.GRADIENT_START_PERC
      const px = x / positions.canvasWidth
      const po = easeBell([step, 1 - step], px)
      return po * DRAW_CONSTS.MAX_OPACITY
    },

    get labelsRightPos() {
      return this.canvasWidth / 2 - DRAW_CONSTS.COUNTRY_WIDTH / 2 - 10
    },
  }).current

  useEffect(() => {
    positions.canvasWidth = parentWidth
  }, [parentWidth])

  return positions
}
